import dibsCss from 'dibs-css';
import { useReducer, type FC, type LegacyRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import SellerApplicationFormSelector from './SellerApplicationFormSelector.tsx';
import { createInitialState, reducer } from './SellerApplicationFormReducer.ts';
import SellerFormContext from './SellerFormContext.tsx';
import ApplyNowButton from './ApplyNowButton.tsx';

import { type SellerApplicationForm_viewer$key } from './__generated__/SellerApplicationForm_viewer.graphql.ts';

import styles from './styles/SellerApplicationForm.scss';

const viewerFragment = graphql`
    fragment SellerApplicationForm_viewer on Viewer
    @argumentDefinitions(leadId: { type: "String!" }) {
        ...SellerApplicationFormSelector_viewer
        readFromSF: featureFlag(feature: "sellerLeadReadFromSF")
        salesforceSellerLead(leadId: $leadId) {
            recordId
            company
            firstName
            lastName
            phone
            email
            country
            state
            city
            street
            postalCode
            primaryBusinessFocus
            eori
            hasExperienceSellingOnline
            businessType
            galleryRepresentation
            whereRepresented
            specialty
            estimatedInventorySize
            currencyISOCode
            averageItemPrice
            numSoldLastYear
            estimatedMonthlyUploads
            isSellerOnOtherMarketPlace
            otherMarketPlaceDetails
            hoursManagePerWeek
            instagram
            facebook
            website
            status
            nmiReason
        }
    }
`;

type Props = {
    viewer: SellerApplicationForm_viewer$key;
    showForm: boolean;
    handleApplyNow: () => void;
    formRef: LegacyRef<HTMLDivElement>;
};

const SellerApplicationForm: FC<Props> = ({
    viewer: viewerRef,
    handleApplyNow,
    showForm,
    formRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const { readFromSF } = viewer;
    const [state, dispatch] = useReducer(reducer, viewer, createInitialState);
    const existingLeadId = viewer.salesforceSellerLead?.recordId;

    return (
        <div
            className={classnames(
                dibsCss.itemsCenter,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.borderT,
                dibsCss.borderL0,
                dibsCss.borderR0,
                dibsCss.borderB0,
                dibsCss.borderDolphin,
                dibsCss.borderSolid,
                dibsCss.ptMedium
            )}
            ref={formRef}
        >
            <div
                className={classnames(
                    styles.formWidth,
                    dibsCss.wFull,
                    dibsCss.mxAuto,
                    dibsCss.textCenter
                )}
            >
                {showForm || (existingLeadId && readFromSF) ? (
                    <SellerFormContext value={{ state, dispatch }}>
                        <SellerApplicationFormSelector viewer={viewer} />
                    </SellerFormContext>
                ) : (
                    <ApplyNowButton onClick={handleApplyNow} />
                )}
            </div>
        </div>
    );
};

export default SellerApplicationForm;
